import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { DeleteAccount } from "@/components/settings/profile/DeleteAccount";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useGetAccount, type PlainAccount } from "@/queries/account-queries";
import { useAuthenticatedUser, type PlainUser } from "@/queries/user-queries";
import { useId, type FC } from "react";

export const UserProfilePage: FC = () => {
    useDocumentTitle("Profile");

    // Explicitly not handling loading/error states for account/user as that's taken care of in AuthenticatedUserLayout
    const { data: account } = useGetAccount();
    const { data: user } = useAuthenticatedUser();

    return (
        <div className="flex flex-col gap-6" data-testid="user-profile-page">
            <UserAvatar user={user} />
            <Name user={user} />
            <EmailAddress account={account || undefined} />
            <AccountId account={account || undefined} />
            <DeleteAccount />
        </div>
    );
};

const UserAvatar: FC<{ user?: PlainUser }> = ({ user }) => {
    return (
        <InputField label="Avatar">
            <Avatar size={48} className="border border-border-base">
                {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={`${user.name || "User"}'s avatar`} />}
                <AvatarFallback>
                    <AvatarInitials name={user?.name || "User"} size={48} />
                </AvatarFallback>
            </Avatar>
        </InputField>
    );
};

const Name: FC<{ user?: PlainUser }> = ({ user }) => {
    return (
        <InputField label="Name">
            <Input disabled={true} value={user?.name} data-testid="name-input" type="text" />
        </InputField>
    );
};

const EmailAddress: FC<{ account?: PlainAccount }> = ({ account }) => {
    return (
        <InputField label="Email">
            <Input disabled={true} value={account?.email} data-testid="email-input" type="text" />
        </InputField>
    );
};

const AccountId: FC<{ account?: PlainAccount }> = ({ account }) => {
    const id = useId();

    return (
        <InputField label="Account ID" id={id}>
            <div className="max-w-[600px]">
                <CopyableInput
                    id={id}
                    title="Copy account ID"
                    className="w-full"
                    value={account?.id ?? ""}
                    copyable
                    disabled
                    data-tracking-id="copy-account-id-user-profile-page"
                />
            </div>
        </InputField>
    );
};
